import { createDinero } from '@mr-yum/frontend-core/dist/services/createDinero'
import { observer } from 'mobx-react-lite'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { useDebounce } from 'react-use'
import { useQuery } from 'urql'

import { sendVisitorEvent } from '@/components/Tracking/VisitorsTracking'
import {
  useOrderingTypeContext,
  useVenueContext,
} from '@/contexts/VenueOrderContext'
import {
  AddToCartMutation,
  MenuItemDetailsDocument,
  MenuItemDetailsVenueDocument,
} from '@/gql/graphql'
import { useCartMenuItem } from '@/hooks/useCart'
import { useLoggerLegacy } from '@/hooks/useLogger'
import { ecommerceEvent } from '@/utils/gtag'

import { ItemDetailsBody } from './ItemDetailsBody'
import { ItemDetailsError } from './ItemDetailsError'
import { ItemDetailsSkeleton } from './ItemDetailsSkeleton'
import { ItemFormController } from './ItemForm'

export interface ItemDetailsProps {
  hasImage?: boolean
  handleClose(item?: AddToCartMutation['addToCart']): void
  startWithADrink?: boolean
}

export const ItemDetails = observer(
  ({ handleClose, hasImage, startWithADrink }: ItemDetailsProps) => {
    const { query } = useRouter()
    const [itemHasImage, setItemHasImage] = useState<boolean>()
    const { orderingType, orderingTypeSlug } = useOrderingTypeContext()
    const { venueSlug } = useVenueContext()
    const categorySlug = String(query.category)
    const itemSlug = String(query.itemSlug)
    const { logEventLegacy } = useLoggerLegacy()
    const sectionSlug = String(query.sectionSlug)

    const [{ data: venueData, error: venueError, fetching: venueFetching }] =
      useQuery({
        query: MenuItemDetailsVenueDocument,
        variables: {
          venueSlug,
          orderingType,
        },
      })

    const venue = venueData?.guestVenue

    const { cart, fetching: cartFetching } = useCartMenuItem({
      venueSlug,
      orderingType,
      pause: !venue,
    })

    const [{ data, error, fetching }] = useQuery({
      query: MenuItemDetailsDocument,
      variables: {
        venueSlug,
        categorySlug,
        itemSlug,
        orderingType,
        tableNumber: cart?.tableNumber ?? undefined,
        tableArea: cart?.tableArea ?? undefined,
        orderingWindowStartDate: cart?.orderingWindowStartDate ?? undefined,
        priceLevel: '',
        sectionSlug,
      },
      pause: !query.itemSlug,
    })

    const menuItem = data?.guestMenuItem
    const category = data?.guestMenuCategory

    const isParentSectionUnavailable = !!category?.menuSections.find(
      (section) =>
        section.slug === sectionSlug &&
        section.menuItems.find((item) => item.slug === menuItem?.slug),
    )?.isUnavailable

    const orderingAvailable = (venue?.ordering &&
      venue?.isOrderingAvailable &&
      venue?.orderingType?.isAvailable &&
      !category?.isClosed) as boolean

    // Keep this value around for modal transitions
    useDebounce(
      () => {
        setItemHasImage(!!menuItem?.image || hasImage)
      },
      300,
      [hasImage, menuItem?.image],
    )

    const hasError = !!venueError || !!error
    const currency = venue?.currency ?? 'AUD'

    useEffect(() => {
      if (menuItem && menuItem.priceData.priceInCents && !hasError) {
        const value = createDinero(
          menuItem.priceData.priceInCents,
          currency,
        ).toUnit()

        if (menuItem.name) {
          void sendVisitorEvent(venueSlug, 'ViewContent', {
            value,
            currency,
            contentName: menuItem.name,
            contentType: 'product',
            contentIds: menuItem.name,
          })

          ecommerceEvent('view_item', {
            value,
            currency,
            items: [
              {
                index: 0,
                price: value,
                item_id: menuItem.id,
                item_name: menuItem.name,
              },
            ],
          })
        }
      }
    }, [
      currency,
      hasError,
      menuItem,
      venueSlug,
      logEventLegacy,
      orderingAvailable,
    ])

    if (hasError) {
      return (
        <ItemDetailsError
          error={(venueError || error)!}
          venueSlug={venueSlug}
          orderingTypeSlug={orderingTypeSlug}
        />
      )
    }

    if (!menuItem || fetching || cartFetching || venueFetching) {
      return <ItemDetailsSkeleton hasImage={itemHasImage} />
    }

    return (
      <ItemFormController
        isAvailable={menuItem.isAvailableForOrdering}
        isItemLevelNotesEnabled={category?.isItemLevelNotesEnabled ?? false}
        itemSlug={itemSlug}
        handleClose={handleClose}
        menuItem={menuItem}
        cart={cart}
        priceLevel=""
        startWithADrink={startWithADrink}
        currency={currency}
      >
        <ItemDetailsBody
          itemSlug={itemSlug}
          orderingAvailable={orderingAvailable}
          isUnavailable={isParentSectionUnavailable}
        />
      </ItemFormController>
    )
  },
)

ItemDetails.displayName = 'ItemDetails'
